import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

import type { LucideIcon } from 'lucide-react';

/** One colour in three uses: the chip itself, a bare dot, and a bare icon or text. */
export interface ChipColor {
  chip: string;
  dot: string;
  text: string;
}

export const CHIP_COLORS = {
  blue: {
    chip: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300',
    dot: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
  },
  violet: {
    chip: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300',
    dot: 'bg-violet-500',
    text: 'text-violet-600 dark:text-violet-400',
  },
  sky: {
    chip: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300',
    dot: 'bg-sky-500',
    text: 'text-sky-600 dark:text-sky-400',
  },
  cyan: {
    chip: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-900 dark:bg-cyan-950 dark:text-cyan-300',
    dot: 'bg-cyan-500',
    text: 'text-cyan-600 dark:text-cyan-400',
  },
  red: {
    chip: 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300',
    dot: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
  },
  amber: {
    chip: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
  },
  green: {
    chip: 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-300',
    dot: 'bg-green-500',
    text: 'text-green-600 dark:text-green-400',
  },
  neutral: {
    chip: 'border-border bg-muted/40 text-muted-foreground',
    dot: 'bg-muted-foreground/60',
    text: 'text-muted-foreground',
  },
} as const satisfies Record<string, ChipColor>;

/** One chip shape for every chip in a conversation and its details: events, gates, tags. For an
 *  element that must be something other than a `StatusChip`, such as a button that opens a panel. */
export const CHIP_SHAPE =
  'inline-flex w-fit shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap';
export const CHIP_ICON = 'pointer-events-none size-3 shrink-0';

/** The chat has no badge of its own, so the transcript and the back office's events, gates and
 *  tags all wear this one chip. A colour outside `CHIP_COLORS` comes in through `className`. */
export function StatusChip({
  icon: Icon,
  color,
  label,
  className,
  ...props
}: Omit<ComponentProps<'span'>, 'color' | 'children'> & {
  icon?: LucideIcon;
  color?: ChipColor;
  label: string;
}) {
  return (
    <span data-slot="badge" {...props} className={cn(CHIP_SHAPE, color?.chip, className)}>
      {Icon && <Icon aria-hidden className={CHIP_ICON} />}
      {label}
    </span>
  );
}

/** Chips set into a conversation between its messages, so events and gates sit there alike.
 *  `role="list"` because `list-none` drops Safari's list semantics. */
export function CenteredChipRow({ className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul
      role="list"
      {...props}
      className={cn(
        'my-2 flex list-none flex-wrap items-center justify-center gap-2 p-0',
        className,
      )}
    />
  );
}
